import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActiveTask, RejectEmailRecipient, TaskReviewView } from '@hcc/types';
import { api } from '../lib/api';

/** One row of the global awaiting-review queue. */
export interface TaskReviewRow {
  person: {
    id: string;
    full_name: string;
    email: string;
    personal_email: string | null;
    position: string | null;
  };
  task: ActiveTask;
}

/** Shared key for the queue, invalidated by task socket events and reviews. */
export const TASK_REVIEWS_KEY = ['admin', 'tasks', 'review'] as const;

/**
 * The global "Tasks to review" queue. Single source for both the queue page
 * and the nav counter. A modest staleTime keeps navigations from re-running
 * the cross-person projection; freshness comes from socket-driven
 * invalidation (see useRealtime) and the approve/reject mutations below.
 */
export function useTaskReviews(enabled = true) {
  return useQuery({
    queryKey: TASK_REVIEWS_KEY,
    enabled,
    staleTime: 30_000,
    queryFn: async () =>
      (await api<{ rows: TaskReviewRow[] }>('/api/admin/tasks/review')).rows,
  });
}

/** The reviewable content for one awaiting-review task (dedicated screen). */
export function useTaskReviewDetail(
  personId: string | undefined,
  taskId: string | undefined,
) {
  return useQuery({
    queryKey: [...TASK_REVIEWS_KEY, personId, taskId],
    enabled: !!personId && !!taskId,
    queryFn: async () =>
      api<TaskReviewView>(`/api/admin/tasks/review/${personId}/${taskId}`),
  });
}

function invalidateReviews(qc: ReturnType<typeof useQueryClient>, personId: string) {
  qc.invalidateQueries({ queryKey: TASK_REVIEWS_KEY });
  qc.invalidateQueries({ queryKey: ['people', personId] });
}

/** Approve a submitted task (TaskApproved). */
export function useApproveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, taskId }: { personId: string; taskId: string }) =>
      api(`/api/people/${personId}/events`, {
        method: 'POST',
        body: JSON.stringify({
          event_type: 'TaskApproved',
          payload: { task_id: taskId },
        }),
      }),
    onSuccess: (_data, { personId }) => invalidateReviews(qc, personId),
  });
}

/** Reject a submitted task with a required reason (TaskRejected). */
export function useRejectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      personId,
      taskId,
      reason,
      recipient,
    }: {
      personId: string;
      taskId: string;
      reason: string;
      recipient?: RejectEmailRecipient;
    }) =>
      api(`/api/people/${personId}/events`, {
        method: 'POST',
        body: JSON.stringify({
          event_type: 'TaskRejected',
          payload: {
            task_id: taskId,
            reason,
            ...(recipient ? { recipient } : {}),
          },
        }),
      }),
    onSuccess: (_data, { personId }) => invalidateReviews(qc, personId),
  });
}
